/**
 * PRODUCT_DEV-1238 メールの無い人のパスワード再発行 API（認証不要）。
 *
 * 認証不要の endpoint なので apiPost（Bearer 自動付与）ではなく fetch を直接使う（resolveAlias と同じ）。
 */
import type {
  PasswordReissueRedeemBody,
  PasswordReissueRequestBody,
} from "@shared-domain/auth-service/contract";
import { AUTH_PASSWORD_REISSUE_REDEEM, AUTH_PASSWORD_REISSUE_REQUEST } from "@/utils/const";

const post = (url: string, body: unknown) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/**
 * 管理者に依頼する。応答は誰の ID でも同じなので、通信が通れば成功として扱う。
 * 通信に失敗したときだけ投げる（もう一度押してもらう）。
 */
export const requestPasswordReissue = async (body: PasswordReissueRequestBody): Promise<void> => {
  const res = await post(AUTH_PASSWORD_REISSUE_REQUEST, body);
  if (!res.ok) throw new Error("依頼を送れませんでした。時間をおいてもう一度お試しください。");
};

/** コードが通らなかった理由（画面はこれで文を選ぶ）。 */
export class PasswordReissueRedeemError extends Error {
  readonly reason: "invalid" | "invalid-password" | "unknown";
  constructor(message: string, reason: "invalid" | "invalid-password" | "unknown") {
    super(message);
    this.name = "PasswordReissueRedeemError";
    this.reason = reason;
  }
}

/** コードで新しいパスワードを決める。失敗は `PasswordReissueRedeemError`。 */
export const redeemPasswordReissue = async (body: PasswordReissueRedeemBody): Promise<void> => {
  const res = await post(AUTH_PASSWORD_REISSUE_REDEEM, body);
  if (res.ok) return;
  let message = "パスワードを設定できませんでした。時間をおいてもう一度お試しください。";
  let reason: PasswordReissueRedeemError["reason"] = "unknown";
  try {
    const data = (await res.json()) as { message?: unknown; reason?: unknown };
    if (typeof data?.message === "string" && data.message) message = data.message;
    if (data?.reason === "invalid" || data?.reason === "invalid-password") reason = data.reason;
  } catch {
    // 本文が読めなければ既定の文言
  }
  throw new PasswordReissueRedeemError(message, reason);
};
